import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Typography,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Grid,
  TablePagination,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  Visibility,
  VisibilityOff,
  PersonOff,
  PersonAdd,
} from '@mui/icons-material';
import api from '../../services/api';

/**
 * Componente de Gestión de Usuarios con Filtros
 * Path: backoffice-web/src/pages/Usuarios/Usuarios.js
 * Última modificación: 2025-12-31 00:00
 * Dev: cpalacios01@gmail.com | +595994648273
 */

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('todos');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    role_id: 2,
    activo: true,
  });

  // Roles disponibles
  const roles = [
    { id: 1, nombre: 'Administrador', color: 'error' },
    { id: 2, nombre: 'Conductor', color: 'primary' },
    { id: 3, nombre: 'Rendidor', color: 'warning' },
  ];

  useEffect(() => {
    fetchUsuarios();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [usuarios, searchTerm, roleFilter, estadoFilter]);

  const fetchUsuarios = async () => {
    try {
      const response = await api.get('/users');
      setUsuarios(response.data.data || response.data);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      setError('Error al cargar los usuarios');
    }
  };

  const applyFilters = () => {
    let filtered = [...usuarios];

    // Filtro de búsqueda
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(usuario => 
        (usuario.nombre?.toLowerCase() || '').includes(search) ||
        (usuario.email?.toLowerCase() || '').includes(search)
      );
    }

    // Filtro de rol
    if (roleFilter !== 'todos') {
      filtered = filtered.filter(usuario => usuario.role_id === parseInt(roleFilter));
    }

    // Filtro de estado
    if (estadoFilter === 'activo') {
      filtered = filtered.filter(usuario => usuario.activo === true || usuario.activo === 1);
    } else if (estadoFilter === 'inactivo') {
      filtered = filtered.filter(usuario => usuario.activo === false || usuario.activo === 0);
    }

    setFilteredUsuarios(filtered);
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
  };

  const handleEstadoFilterChange = (e) => {
    setEstadoFilter(e.target.value);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('todos');
    setEstadoFilter('todos');
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      password: '',
      role_id: 2,
      activo: true,
    });
  };

  const handleOpenDialog = (usuario = null) => {
    if (usuario) {
      setEditingUsuario(usuario);
      setFormData({
        nombre: usuario.nombre || '',
        email: usuario.email || '',
        password: '',
        role_id: usuario.role_id || 2,
        activo: usuario.activo ?? true,
      });
    } else {
      setEditingUsuario(null);
      resetForm();
    }
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUsuario(null);
    resetForm();
    setError('');
    setShowPassword(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const dataToSend = { ...formData };
      
      // Si no hay password al editar, no enviarlo
      if (editingUsuario && !dataToSend.password) {
        delete dataToSend.password;
      }
      
      // Validar password al crear
      if (!editingUsuario && !dataToSend.password) {
        setError('La contraseña es requerida');
        setLoading(false);
        return;
      }

      if (editingUsuario) {
        await api.put(`/users/${editingUsuario.id}`, dataToSend);
        setSuccess('Usuario actualizado exitosamente');
      } else {
        await api.post('/users', dataToSend);
        setSuccess('Usuario creado exitosamente');
      }

      await fetchUsuarios();
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);

    } catch (error) {
      console.error('Error al guardar usuario:', error);
      
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join(', ');
        setError(`Error de validación: ${errorMessages}`);
      } else {
        setError(error.response?.data?.message || 'Error al guardar el usuario');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActivo = async (id) => {
    if (!window.confirm('¿Está seguro de cambiar el estado de este usuario?')) {
      return;
    }

    try {
      await api.post(`/users/${id}/toggle-activo`);
      setSuccess('Estado actualizado exitosamente');
      fetchUsuarios();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      setError(error.response?.data?.message || 'Error al cambiar el estado');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) {
      return;
    }

    try {
      await api.delete(`/users/${id}`);
      setSuccess('Usuario eliminado exitosamente');
      fetchUsuarios();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      setError(error.response?.data?.message || 'Error al eliminar el usuario');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.nombre : 'Desconocido';
  };

  const getRoleColor = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.color : 'default';
  };

  const paginatedUsuarios = filteredUsuarios.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Gestión de Usuarios</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Usuario
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Filtros y búsqueda */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre o email..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Rol</InputLabel>
              <Select
                value={roleFilter}
                label="Rol"
                onChange={handleRoleFilterChange}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                }
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="1">Administrador</MenuItem>
                <MenuItem value="2">Conductor</MenuItem>
                <MenuItem value="3">Rendidor</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={estadoFilter}
                label="Estado"
                onChange={handleEstadoFilterChange}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="activo">Activo</MenuItem>
                <MenuItem value="inactivo">Inactivo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleClearFilters}
            >
              Limpiar Filtros
            </Button>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {filteredUsuarios.length} de {usuarios.length} usuarios
          </Typography>
        </Box>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsuarios.map((usuario) => (
              <TableRow key={usuario.id}>
                <TableCell>{usuario.id}</TableCell>
                <TableCell>{usuario.nombre}</TableCell>
                <TableCell>{usuario.email}</TableCell>
                <TableCell>
                  <Chip
                    label={getRoleName(usuario.role_id)}
                    color={getRoleColor(usuario.role_id)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={usuario.activo ? 'Activo' : 'Inactivo'}
                    color={usuario.activo ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(usuario)}
                    title="Editar"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color={usuario.activo ? 'warning' : 'success'}
                    onClick={() => handleToggleActivo(usuario.id)}
                    title={usuario.activo ? 'Desactivar' : 'Activar'}
                  >
                    {usuario.activo ? <PersonOff /> : <PersonAdd />}
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(usuario.id)}
                    title="Eliminar"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedUsuarios.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {searchTerm || roleFilter !== 'todos' || estadoFilter !== 'todos'
                    ? 'No se encontraron usuarios con los filtros aplicados'
                    : 'No hay usuarios registrados'
                  }
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredUsuarios.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </TableContainer>

      {/* Diálogo crear/editar usuario */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editingUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre Completo *"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={editingUsuario ? "Contraseña (dejar vacío para no cambiar)" : "Contraseña *"}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required={!editingUsuario}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Rol *</InputLabel>
                  <Select
                    name="role_id"
                    value={formData.role_id}
                    label="Rol *"
                    onChange={handleChange}
                  >
                    {roles.map((rol) => (
                      <MenuItem key={rol.id} value={rol.id}>
                        {rol.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.activo}
                      onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                    />
                  }
                  label="Usuario Activo"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.nombre || !formData.email}
          >
            {loading ? 'Guardando...' : editingUsuario ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// cpalacios01@gmail.com | +595994648273
